

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckedInPatientListParams, generateStockBarcode, getAvailableStockList, getAvailableStockListItem, getAvailableStockListCount, getAvailableStockListForReport, GetAvailableStockListParams, getCheckedInPatientList, getClinicList, GetClinicListParams, getInventoryItems, GetInventoryItemsParams, getItemCategoryList, getItemGroupList, getPatientPackages, ItemCategoryParams, itemgroupParams, list_header_type_details } from '../Common/inventory.helper';
import { createOpeningBalance } from '../Stocks/stock.helper';
import { createMaterialConsumption, getConsumptionLogList, GetConsumptionLogParams } from '../MaterialConsumption/MaterialConsumption.helper';
import { useDebounce } from '../hooks/useDebounce';


//get clinic list
export const useClinicList = (params: GetClinicListParams = {}) => {
  return useQuery({
    queryKey: ['clinics', params],  // dynamic caching by params
    queryFn: () => getClinicList(params),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       // cache 5 min
  });
};



//get item group list
export const useItemGroupList = (params: itemgroupParams = {}) => {
  return useQuery({
    queryKey: ['itemGroup', params],  // dynamic caching by params
    queryFn: () => getItemGroupList(params),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       // cache 5 min
  });
};



//get item category list
export const useItemCategoryList = (params: ItemCategoryParams = {}) => {
  return useQuery({
    queryKey: ['itemCategory', params],  // dynamic caching by params
    queryFn: () => getItemCategoryList(params),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       // cache 5 min
  });
};



//get inventory items
export const useInventoryItems = (params: GetInventoryItemsParams) => {
  const shouldFetch = params.clinic_id > 0 && params.dept_id > 0;

  return useQuery({
    queryKey: ['inventoryItems', params],  
    queryFn: () => getInventoryItems(params),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       
    enabled: shouldFetch,      
  });
};


//create opening balance / add stock
export function useCreateOpeningBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOpeningBalance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availableStockList'] });   // get available stock list after adding new opening balance
    },
    retry: 0, 
  });
}



//get available Stock list
export const useAvailableStockList = (params: GetAvailableStockListParams & { enabled?: boolean } = {}) => {
  const { enabled = true, ...queryParams } = params;

  return useQuery({
    queryKey: [
      'availableStockList',
      queryParams.limit,
      queryParams.offset,
      queryParams.search,
      queryParams.from_date,
      queryParams.to_date,
      queryParams.is_low_stock,
      queryParams.is_near_expiry,
      queryParams.is_no_stock,
      queryParams.is_expired,
      queryParams.list_header_type,
      queryParams.clinic_id,
      queryParams.dept_id,
      queryParams.item_group_id,
      queryParams.item_category_id,
    ],
    queryFn: () => getAvailableStockList(queryParams),
    retry: 1,
    staleTime: 0,       // Always consider stale to force fresh fetches on page changes
    gcTime: 0,          // Don't keep in cache after unused
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled,
  });
};

//get available Stock list itemWise (for StockDetailsPage)
export const useAvailableStockListItem = (params: GetAvailableStockListParams & { enabled?: boolean } = {}) => {
  const { enabled = true, ...queryParams } = params;

  return useQuery({
    queryKey: [
      'availableStockListItem',
      queryParams.limit,
      queryParams.offset,
      queryParams.search,
      queryParams.from_date,
      queryParams.to_date,
      queryParams.is_low_stock,
      queryParams.is_near_expiry,
      queryParams.is_no_stock,
      queryParams.is_expired,
      queryParams.list_header_type,
      queryParams.clinic_id,
      queryParams.dept_id,
      queryParams.item_group_id,
      queryParams.item_category_id,
    ],
    queryFn: () => getAvailableStockListItem(queryParams),
    retry: 1,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled,
  });
};

//get available stock list count with all filters
export const useAvailableStockListCount = (params: GetAvailableStockListParams & { enabled?: boolean } = {}) => {
  const { enabled = true, ...queryParams } = params;

  return useQuery({
    queryKey: [
      'availableStockListCount',
      queryParams.limit,
      queryParams.offset,
      queryParams.search,
      queryParams.from_date,
      queryParams.to_date,
      queryParams.is_low_stock,
      queryParams.is_near_expiry,
      queryParams.is_no_stock,
      queryParams.list_header_type,
      queryParams.clinic_id,
      queryParams.dept_id,
      queryParams.item_group_id,
      queryParams.item_category_id,
    ],
    queryFn: () => getAvailableStockListCount(queryParams),
    retry: 1,
    staleTime: 0,       // Always consider stale to force fresh fetches
    gcTime: 0,          // Don't keep in cache after unused
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled,
  });
};


//get available items for consumption 
export const useAvailableInventoryItemsForConsumption = (params: GetAvailableStockListParams) => {
  const shouldFetch = params.clinic_id !== undefined && params.dept_id !== undefined;
  return useQuery({
    queryKey: ['availableInventoryItemsForConsumption', params],  
    queryFn: () => getAvailableStockList(params),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       
    enabled: shouldFetch,      
  });
};





//consume inventory items
export function useConsumeMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMaterialConsumption,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availableStockList'] });
      queryClient.invalidateQueries({ queryKey: ['availableInventoryItemsForConsumption'] });
      queryClient.invalidateQueries({ queryKey: ['consumptionLog'] });
    },
    retry: 0, 
  });
}



export const useConsumptionLog = (params: GetConsumptionLogParams = {}) => {
  // Debounce search parameter to reduce API calls
  const debouncedSearch = useDebounce(params.search, 300);
  
  const debouncedParams = {
    ...params,
    search: debouncedSearch
  };

  return useQuery({
    queryKey: ['consumptionLog', debouncedParams],  
    queryFn: () => getConsumptionLogList(debouncedParams),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       // cache 5 min
  });
};



export const useCheckedInPatientList = (params: CheckedInPatientListParams) => {
  return useQuery({
    queryKey: ['checkedInPatientList', params],  
    queryFn: () => getCheckedInPatientList(params),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       // cache 5 min
  });
};


export const usePatientPackages = (patient_id : number) => {
  return useQuery({
    queryKey: ['patientPackages', patient_id],  
    queryFn: () => getPatientPackages(patient_id),
    retry: 1, // 1 retry on fail
    staleTime: 1000 * 60 * 5,       // cache 5 min
    enabled: patient_id !== null && patient_id > 0,      
  });
};


//generate Barcode for selected batch
export function useGenerateBarcode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateStockBarcode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availableStockList'] });
    },
    retry: 0, 
  });
}


//get available stock list Report
export const useAvailableStockListReport = (type: list_header_type_details, clinicId?: number) => {
  return useQuery({
    queryKey: ['availableStockListReport', type, clinicId],  
    queryFn: () => getAvailableStockListForReport(type, clinicId),
    retry: 0, // 0 retry on fail
    staleTime: 1000 * 60 * 5,       // cache 5 min
  });
};
